import { useSupabaseClient } from "@supabase/auth-helpers-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createRecordRequestAuthed } from "../api/record-requests.api";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import Input from "../components/shared/input";
import Typography from "../components/shared/typography";
import { recordRequestPayloadSchema } from "../request-records/schema";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";

type IdentityVerifyGateContextValue = {
  showGate: boolean;
};

const IdentityVerifyGateContext = createContext<
  IdentityVerifyGateContextValue | undefined
>(undefined);

export function useIdentityVerifyGate() {
  const ctx = useContext(IdentityVerifyGateContext);
  if (!ctx)
    throw new Error(
      "useIdentityVerifyGate must be used within IdentityVerifyGateProvider"
    );
  return ctx;
}

export function IdentityVerifyGateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const supabase = useSupabaseClient();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const postedRef = useRef(false);

  const showGate = useMemo(() => !isAuthenticated, [isAuthenticated]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qEmail = params.get("email");
    if (qEmail && !email) setEmail(qEmail);
  }, [location.search, email]);

  async function postRecordRequestDraftIfPresent() {
    try {
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect") || "";
      let slug: string | undefined;
      try {
        const url = new URL(redirect, window.location.origin);
        const pathname = url.pathname;
        const parts = pathname.split("/").filter(Boolean);
        if (parts[0] === "request-records" && parts[1])
          slug = decodeURIComponent(parts[1]);
      } catch {
        const pathname = redirect.split("?")[0] || "";
        const parts = pathname.split("/").filter(Boolean);
        if (parts[0] === "request-records" && parts[1])
          slug = decodeURIComponent(parts[1]);
      }
      if (!slug) return;
      const storageKey = `request-records:${slug}`;
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const payload = recordRequestPayloadSchema.parse(
        parsed &&
          typeof parsed === "object" &&
          (parsed.form || parsed.step !== undefined)
          ? parsed
          : {
              form: parsed,
              step: 0,
              status: {
                identityVerified: false,
                signatureCompleted: false,
                phase: 1,
              },
            }
      );
      await createRecordRequestAuthed(payload);
      postedRef.current = true;
    } catch (e) {
      console.warn("Failed to post record request draft:", e);
      showToast("Couldn’t save your draft to server", "error");
    }
  }

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || (event === "USER_UPDATED" && session)) {
        if (!postedRef.current) {
          void postRecordRequestDraftIfPresent();
        }
        const params = new URLSearchParams(location.search);
        const redirect =
          params.get("redirect") ||
          `${location.pathname}${location.search}${location.hash}`;
        const emailParam = params.get("email")
          ? `&email=${encodeURIComponent(params.get("email") as string)}`
          : "";
        if (location.pathname !== "/identity/verify") {
          navigate(
            `/identity/verify?redirect=${encodeURIComponent(
              redirect
            )}${emailParam}`,
            { replace: true }
          );
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [
    navigate,
    location.pathname,
    location.search,
    location.hash,
    supabase.auth,
  ]);

  async function handleContinue() {
    if (!email || !password) {
      showToast("Email and password are required", "error");
      return;
    }
    if (password !== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({ email, password });
      if (signUpError) {
        showToast(signUpError.message || "Failed to create account", "error");
      } else {
        if (signUpData.session) {
          showToast("Account created", "success");
        } else {
          showToast(
            "Account created. Please check email to confirm.",
            "success"
          );
        }
      }
    } catch (e: any) {
      showToast(e?.message || "Authentication failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <IdentityVerifyGateContext.Provider value={{ showGate }}>
      <div className="relative">
        {children}
        {showGate && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-white/20 p-4 pt-24 backdrop-blur-sm md:pt-32">
            <div className="pointer-events-auto w-full max-w-md">
              <Card className="w-full" variant="cream" elevation={2}>
                <Card.Header variant="cream" className="text-center">
                  <Typography variant="h5" className="text-brown-900">
                    Create a password to continue
                  </Typography>
                  <Typography variant="body2" className="mt-1 text-brown-700">
                    Set your email and password to verify your identity and save
                    your progress.
                  </Typography>
                </Card.Header>
                <Card.Body className="space-y-3">
                  <Input
                    type="email"
                    label="Email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-terracotta-200 text-brown-900 placeholder:text-brown-400 hover:border-terracotta-300 focus:border-terracotta-500"
                    labelClassName="text-terracotta-700"
                    helperTextClassName="text-terracotta-600"
                    fullWidth
                  />
                  <Input
                    type="password"
                    label="Password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border-terracotta-200 text-brown-900 placeholder:text-brown-400 hover:border-terracotta-300 focus:border-terracotta-500"
                    labelClassName="text-terracotta-700"
                    helperTextClassName="text-terracotta-600"
                    fullWidth
                  />
                  <Input
                    type="password"
                    label="Confirm Password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="border-terracotta-200 text-brown-900 placeholder:text-brown-400 hover:border-terracotta-300 focus:border-terracotta-500"
                    labelClassName="text-terracotta-700"
                    helperTextClassName="text-terracotta-600"
                    fullWidth
                  />
                </Card.Body>
                <Card.Footer
                  variant="cream"
                  className="flex flex-col items-end gap-4"
                >
                  <SubmitButton
                    color="terracotta"
                    size="md"
                    onClick={handleContinue}
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting || !password}
                  >
                    Continue
                  </SubmitButton>
                  <div className="flex items-center gap-2">
                    <Typography variant="caption" className="text-brown-700">
                      Already have an account?
                    </Typography>
                    <SubmitButton
                      color="cream"
                      size="sm"
                      onClick={() => {
                        const redirect = `${location.pathname}${location.search}${location.hash}`;
                        navigate(
                          `/login?redirect=${encodeURIComponent(redirect)}`
                        );
                      }}
                    >
                      Log in
                    </SubmitButton>
                  </div>
                </Card.Footer>
              </Card>
            </div>
          </div>
        )}
      </div>
    </IdentityVerifyGateContext.Provider>
  );
}
